<template>
  <div class="inline-block">
    <a-badge :count="unreadCount" :dot="true">
      <a-popover placement="bottomRight" trigger="hover" :overlay-style="{ marginLeft: '-50px' }" size="small">
        <template #content>
          <template v-if="messages.length === 0">
            <a-empty :description="t('header.message.no_message')" />
          </template>
          <template v-else>
            <a-list :data-source="messages" size="small">
              <template #header>
                <div class="h-4 flex items-center justify-between border-b px-[16px]">
                  <div class="flex items-center space-x-2">
                    <span class="text-lg font-700 text-gray-800">
                      {{ t('header.message.title') }}
                    </span>
                  </div>
                  <div>
                    <a-button
                      v-show="readCount > 0"
                      class="px-0"
                      type="dashed"
                      size="small"
                      color="red"
                      @click="deleteReadNotification"
                    >
                      {{ t('header.message.delete_read') }}
                    </a-button>
                    <a-button
                      v-show="unreadCount > 0"
                      class="ml-2 px-0"
                      type="dashed"
                      size="small"
                      color="blue"
                      @click="markAllAsRead"
                    >
                      {{ t('header.message.mark_all_as_read') }}
                    </a-button>
                  </div>
                </div>
              </template>
              <template #renderItem="{ item }">
                <template v-if="!item.isDeleted">
                  <a-list-item class="transition-all hover:bg-blue-100" :class="item.isRead ? '' : 'bg-gray-100'">
                    <div class="w-full flex justify-between" :class="item.isRead ? ' opacity-60' : ''">
                      <div class="flex-1">
                        <div class="flex">
                          <span class="text-[#267CFF]">
                            {{ item.title }}
                          </span>
                          <span class="ml-4 text-gray-400">
                            {{ new Date(item.createdAt * 1000).toLocaleString() }}
                          </span>
                        </div>
                        <div class="ml-2 text-[13px] max-w-80 flex-col-center">
                          {{ item.content }}
                        </div>
                      </div>
                      <div class="mx-3 border-l border-gray-300" style="border: 1px dashed #e0e0e0"></div>
                      <div
                        :class="[
                          item.isRead ? 'flex flex-col items-center justify-center' : 'flex flex-col items-center',
                        ]"
                      >
                        <a-button
                          class="px-0 text-[13px] flex-col-center"
                          type="link"
                          :href="item.link"
                          target="_blank"
                          @click="markAsRead(item)"
                        >
                          {{ t('header.message.go_to_handle') }}
                        </a-button>
                        <a-button
                          v-if="!item.isRead"
                          class="px-0 text-[13px] text-gray-500"
                          type="link"
                          @click="markAsRead(item)"
                        >
                          {{ t('header.message.mark_as_read') }}
                        </a-button>
                      </div>
                    </div>
                  </a-list-item>
                </template>
              </template>
            </a-list>
          </template>
        </template>
        <a type="link" class="notification-trigger" :class="{ 'blinking': unreadCount > 0 }">
          <bell-outlined />
          <span>{{ t('header.message.title') }}</span>
        </a>
      </a-popover>
    </a-badge>
  </div>
</template>

<script setup lang="ts">
import { notificationApi } from '@/api/notification';
import { useUserStore } from '@/store';
import { BellOutlined } from '@ant-design/icons-vue';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
interface Message {
  id: number;
  title: string;
  content: string;
  createAt: string;
  isRead: boolean;
  link: string;
  isDeleted: boolean;
}

const user = useUserStore();

const messages = ref<Message[]>([]);

const unreadCount = computed(() => {
  return messages.value.filter((msg) => !msg.isRead && !msg.isDeleted).length;
});

// 已读消息数量
const readCount = computed(() => {
  return messages.value.filter((msg) => msg.isRead && !msg.isDeleted).length;
});

const markAllAsRead = async () => {
  try {
    const res = await notificationApi.markNotificationIsRead(messages.value.map((msg) => msg.id));
    if (res.code !== 0) {
      return;
    }
    messages.value.forEach((msg) => (msg.isRead = true));
  } catch (error) {
    console.error(error);
  }
};

// 删除已读消息
const deleteReadNotification = async () => {
  try {
    const res = await notificationApi.deleteReadNotification(user.current.id);
    if (res.code !== 0) {
      return;
    }
    // 更新 messages 列表，过滤掉已删除的消息
    messages.value = messages.value.filter((msg) => !msg.isRead || msg.isDeleted);
  } catch (error) {
    console.error(error);
  }
};

const markAsRead = async (item: Message) => {
  if (item.isRead) {
    return;
  }

  try {
    const res = await notificationApi.markNotificationIsRead([item.id]);
    if (res.code !== 0) {
      return;
    }
    item.isRead = true;
  } catch (error) {
    console.error(error);
  }
};

onMounted(async () => {
  // 发起请求获取获取信息list
  const res = await notificationApi.getNotificationList(user.current.id);
  if (res.code !== 0) {
    return;
  }
  messages.value = res.data;
});
</script>

<style scoped>
:deep(.ant-list-items) {
  max-height: 400px;
  overflow: auto;
}

.notification-trigger {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #86909c;
  font-size: 13px;
  cursor: pointer;
  text-decoration: none;
  transition: color 0.2s;
}

.notification-trigger:hover {
  color: #fff;
}

.blinking {
  animation: blinkingText 0.8s infinite;
}

@keyframes blinkingText {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}
</style>
